import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { API } from "../../constants";

const VisitorTracker = () => {
  const location = useLocation();
  const visitIdRef = useRef(null);
  const startTimeRef = useRef(null);
  const [settings, setSettings] = useState(null);
  
  // Fetch settings to check if location tracking is enabled
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings`);
        setSettings(res.data);
      } catch (error) {
        console.error("Error fetching settings:", error);
      }
    };
    fetchSettings();
  }, []);
  
  // Get user location if permission granted
  const getUserLocation = () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }
      
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            // Use reverse geocoding to get location details
            const { latitude, longitude } = position.coords;
            
            // Use a free reverse geocoding API with more details
            const geoRes = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=14&addressdetails=1`,
              {
                headers: {
                  'User-Agent': 'CSBPathway/1.0'
                }
              }
            );
            const geoData = await geoRes.json();
            const addr = geoData.address || {};
            
            // Extract city - try multiple fields for Bangladesh/South Asia
            const city = addr.city 
              || addr.town 
              || addr.village 
              || addr.municipality
              || addr.suburb
              || addr.neighbourhood
              || addr.state_district  // For Bangladesh districts
              || addr.county
              || addr.locality
              || geoData.name  // Fallback to location name
              || "Unknown";
            
            // Extract region/division
            const region = addr.state 
              || addr.division 
              || addr.region
              || addr.province
              || "Unknown";
            
            console.log("Location captured:", { city, region, country: addr.country });
            
            resolve({
              latitude,
              longitude,
              city: city,
              region: region,
              country: addr.country || "Unknown",
              country_code: addr.country_code?.toUpperCase() || "XX"
            });
          } catch (error) {
            console.error("Geocoding error:", error);
            resolve({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              city: "Unknown",
              region: "Unknown",
              country: "Unknown",
              country_code: "XX"
            });
          }
        },
        (error) => {
          console.log("Location permission denied or error:", error.message);
          resolve(null);
        },
        {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 300000 // Cache for 5 minutes
        }
      );
    });
  };
  
  useEffect(() => {
    // Generate or get session ID
    let sessionId = sessionStorage.getItem('visitor_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('visitor_session_id', sessionId);
    }
    
    // Record visit
    const recordVisit = async () => {
      startTimeRef.current = Date.now();
      
      let locationData = null;
      
      // Check if location tracking is enabled in settings
      if (settings?.location_tracking_enabled) {
        locationData = await getUserLocation();
      }
      
      try {
        const visitData = {
          session_id: sessionId,
          page_path: location.pathname,
          referrer: document.referrer || null,
          user_agent: navigator.userAgent,
          duration_seconds: 0
        };
        
        // Add location data if available
        if (locationData) {
          visitData.location_city = locationData.city;
          visitData.location_region = locationData.region;
          visitData.location_country = locationData.country;
          visitData.location_country_code = locationData.country_code;
          visitData.latitude = locationData.latitude;
          visitData.longitude = locationData.longitude;
        }
        
        const res = await axios.post(`${API}/traffic/visit`, visitData);
        visitIdRef.current = res.data.id;
      } catch (error) {
        console.error("Error recording visit:", error);
      }
    };
    
    // Only record after settings are loaded
    if (settings !== null) {
      recordVisit();
    }
    
    // Update duration on page leave
    const updateDuration = async () => {
      if (visitIdRef.current && startTimeRef.current) {
        const duration = Math.round((Date.now() - startTimeRef.current) / 1000);
        try {
          await axios.put(`${API}/traffic/visit/${visitIdRef.current}?duration_seconds=${duration}`);
        } catch (error) {
          console.error("Error updating duration:", error);
        }
      }
    };
    
    // Handle page leave
    window.addEventListener('beforeunload', updateDuration);
    
    return () => {
      updateDuration();
      window.removeEventListener('beforeunload', updateDuration);
    };
  }, [location.pathname, settings]);
  
  return null; // This component doesn't render anything
};

export default VisitorTracker;
